import type { Key } from 'react';
import { useContext, useMemo, useRef } from 'react';

import type { DateValue } from 'react-aria-components/Calendar';
import {
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarStateContext,
  Button as RACButton,
} from 'react-aria-components/Calendar';
import { useLocale } from 'react-aria-components/I18nProvider';
import { ListBox, ListBoxItem } from 'react-aria-components/ListBox';
import { RangeCalendarStateContext } from 'react-aria-components/RangeCalendar';
import { Select, SelectValue } from 'react-aria-components/Select';

import { cx } from '../../../utils/cx';
import { Button as UADButton } from '../../button/button';
import { Icon } from '../../icon/icon';
import { Popover } from '../../popover/popover';
import { Text } from '../../text/text';

import styles from './calendar_content.module.css';

type CalendarSelectItem = {
  value: number | string;
  label: string;
};

type CalendarSelectProps = {
  ariaLabel: string;
  items: CalendarSelectItem[];
  selectedKey: string;
  onSelectionChange: (key: Key | null) => void;
  className?: string;
};

function CalendarSelect({
  ariaLabel,
  items,
  selectedKey,
  onSelectionChange,
  className,
}: CalendarSelectProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Select
      aria-label={ariaLabel}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChange}
      className={cx([styles.select, className])}
    >
      <RACButton ref={buttonRef} className={styles.select_button}>
        <SelectValue className={styles.select_value} />
        <Icon name="chevron_small_isolated_s" size="s" />
      </RACButton>

      <Popover
        className={styles.select_popover}
        offset={2}
        maxHeight={210}
        style={
          buttonRef.current
            ? {
                '--trigger-width': `${buttonRef.current.offsetWidth}px`,
              }
            : undefined
        }
      >
        <ListBox className={styles.select_listbox} items={items}>
          {(item) => (
            <ListBoxItem
              id={item.value.toString()}
              textValue={item.label}
              className={styles.select_item}
            >
              <Icon
                name="checkmark"
                size="xs"
                className={styles.select_checkmark}
              />
              <div className={styles.select_item_label_container}>
                <Text elementType="span" variant="value">
                  {item.label}
                </Text>
              </div>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}

type IsMonthInBoundsOptions = {
  year: number;
  monthIndex: number;
  minValue?: DateValue | null;
  maxValue?: DateValue | null;
};

function isMonthInBounds({
  year,
  monthIndex,
  minValue,
  maxValue,
}: IsMonthInBoundsOptions): boolean {
  const month = monthIndex + 1;

  if (
    minValue &&
    (year < minValue.year || (year === minValue.year && month < minValue.month))
  ) {
    return false;
  }

  return !(
    maxValue &&
    (year > maxValue.year || (year === maxValue.year && month > maxValue.month))
  );
}

function MonthPicker() {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState ?? rangeCalendarState;
  const { locale } = useLocale();

  const months = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(2000, i, 1);
      return { value: i, label: formatter.format(date) };
    });
  }, [locale]);

  if (!state) {
    return null;
  }

  const currentMonth = state.focusedDate.month - 1;
  const selectableMonths = months.filter((month) =>
    isMonthInBounds({
      year: state.focusedDate.year,
      monthIndex: month.value,
      minValue: state.minValue,
      maxValue: state.maxValue,
    }),
  );

  const handleChange = (key: Key | null) => {
    if (!key) {
      return;
    }
    state.setFocusedDate(state.focusedDate.set({ month: Number(key) + 1 }));
  };

  return (
    <CalendarSelect
      ariaLabel="Month"
      items={selectableMonths}
      selectedKey={currentMonth.toString()}
      onSelectionChange={handleChange}
      className={styles.month_picker}
    />
  );
}

function YearPicker() {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState ?? rangeCalendarState;

  const years = useMemo(() => {
    if (!state) {
      return [];
    }
    const current = state.focusedDate.year;
    const first = Math.max(current - 10, state.minValue?.year ?? -Infinity);
    const last = Math.min(current + 10, state.maxValue?.year ?? Infinity);

    return Array.from({ length: Math.max(last - first + 1, 0) }, (_, index) => {
      const year = first + index;
      return { value: year, label: year.toString() };
    });
  }, [state]);

  if (!state) {
    return null;
  }

  const handleChange = (key: Key | null) => {
    if (!key) {
      return;
    }
    state.setFocusedDate(state.focusedDate.set({ year: Number(key) }));
  };

  return (
    <CalendarSelect
      ariaLabel="Year"
      items={years}
      selectedKey={state.focusedDate.year.toString()}
      onSelectionChange={handleChange}
      className={styles.year_picker}
    />
  );
}

export function CalendarContent() {
  const { locale } = useLocale();
  const isEnglish = locale.toLowerCase().startsWith('en');

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <UADButton
          slot="previous"
          variant="tertiary"
          aria-label="Previous month"
          icon={<Icon name="chevron_large_isolated_w" size="s" />}
        />

        <div className={styles.month_year_container}>
          <MonthPicker />
          <YearPicker />
        </div>

        <UADButton
          slot="next"
          variant="tertiary"
          aria-label="Next month"
          icon={<Icon name="chevron_large_isolated_e" size="s" />}
        />
      </header>

      <CalendarGrid className={styles.grid} weekdayStyle="short">
        <CalendarGridHeader>
          {(day) => {
            const normalizedDay = isEnglish ? day.replace(/\.$/, '') : day;
            const displayDay = isEnglish ? normalizedDay.slice(0, 2) : day;
            return (
              <CalendarHeaderCell className={styles.weekday_cell}>
                {displayDay}
              </CalendarHeaderCell>
            );
          }}
        </CalendarGridHeader>

        <CalendarGridBody>
          {(date) => (
            <CalendarCell date={date} className={styles.day_cell}>
              {({ formattedDate }) => (
                <span className={styles.day_cell_inner}>{formattedDate}</span>
              )}
            </CalendarCell>
          )}
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  );
}
